import { useState } from "react"
import { Pressable, View, ActivityIndicator } from "react-native"
import MIcon from "@react-native-vector-icons/material-design-icons"
import * as Burnt from "burnt"
import { trpc } from "@src/utils/trpc"
import { ar } from "@src/i18n/ar"
import { Text, TextInput, ModalWrapper } from "./core"
import { ImageUploadWrapper } from "./image-upload-wrapper"
import { useUploadImage } from "@src/hooks/use-product-image-upload"

type BrandFormProps = {
  onClose: () => void
  id?: number
}

export function BrandForm({ onClose, id }: BrandFormProps) {
  const utils = trpc.useUtils()
  const isEditMode = id != null
  const brands = trpc.brands.list.useQuery()
  const brand = brands.data?.find((b) => b.id === id)
  const [name, setName] = useState(brand?.name ?? "")

  const createBrand = trpc.brands.create.useMutation()
  const editBrand = trpc.brands.edit.useMutation()
  const uploadImage = useUploadImage()
  const [imageUri, setImageUri] = useState<string | null>(null)

  function handleSubmit() {
    if (!name.trim() || name.trim().length < 2) {
      Burnt.toast({ title: ar.validationError, preset: "error" })
      return
    }

    if (!isEditMode && !imageUri) {
      Burnt.toast({ title: ar.pleaseSelectImage, preset: "error" })
      return
    }

    const onSuccess = (entityId: number) => {
      if (imageUri) {
        uploadImage.mutate(
          { entity: "brand", entityId, uri: imageUri },
          {
            onSuccess: () => {
              utils.brands.list.invalidate()
              utils.menu.get.invalidate()
              onClose()
            },
          },
        )
      } else {
        utils.brands.list.invalidate()
        utils.menu.get.invalidate()
        onClose()
      }
    }

    if (isEditMode) {
      editBrand.mutate({ id, name: name.trim() }, { onSuccess: () => onSuccess(id) })
    } else {
      createBrand.mutate({ name: name.trim() }, { onSuccess: (result) => onSuccess(result.id) })
    }
  }

  const isPending = createBrand.isPending || editBrand.isPending || uploadImage.isPending

  return (
    <ModalWrapper onClose={onClose}>
      <View className="p-4 gap-4">
        <Text className="text-xl font-bold">{isEditMode ? ar.editBrand : ar.newBrand}</Text>
        <TextInput placeholder={ar.brandName} value={name} onChangeText={setName} autoFocus />

        <ImageUploadWrapper processing={uploadImage.isPending} onConfirm={(uri) => setImageUri(uri)}>
          {imageUri ? (
            <View className="w-full h-40 bg-gray-100 rounded items-center justify-center">
              <MIcon name="check-circle" size={48} color="#34A85A" />
              <Text className="text-gray-600 mt-2">{ar.imageSelected}</Text>
            </View>
          ) : (
            <View className="w-full h-40 bg-gray-100 rounded items-center justify-center border-2 border-dashed border-gray-300">
              <MIcon name="image-plus" size={48} color="#999" />
              <Text className="text-gray-500 mt-2">{ar.tapToUploadImage}</Text>
            </View>
          )}
        </ImageUploadWrapper>

        <Pressable className="bg-blue-500 py-3 px-4 rounded items-center active:opacity-70" onPress={handleSubmit} disabled={isPending || name.trim().length < 2}>
          {isPending ? <ActivityIndicator color="white" /> : <Text className="text-white font-bold">{isEditMode ? ar.save : ar.create}</Text>}
        </Pressable>
      </View>
    </ModalWrapper>
  )
}
